import React, { useState } from 'react';
import { Alert, Button, Skeleton, Switch, message } from 'antd';
import { Link, Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import FeaturePage from '@/components/featurepage';
import { fetchNotificationPreferences, updateNotificationPreferences } from '@/apis/notifications';
import { refreshNotificationSummary } from '@/store/modules/notifications';
import usePrivateList from '@/utils/usePrivateList';
import usePrivateAction from '@/utils/usePrivateAction';
import './index.scss';

const settingGroups = [
  {
    key: 'interactions', title: '互动提醒', description: '关注帖子和评论中的互动反馈。',
    items: [
      { key: 'likes_enabled', label: '点赞提醒', description: '有人点赞你的帖子时提醒你。' },
      { key: 'comments_enabled', label: '评论与回复提醒', description: '有人评论你的帖子或回复你的评论时提醒你。' },
    ],
  },
  {
    key: 'activities', title: '招募与活动', description: '及时了解申请变化和活动进度。',
    items: [
      { key: 'recruitment_enabled', label: '招募申请提醒', description: '申请获批、被拒绝、撤回或重新提交时提醒你。' },
      { key: 'activity_enabled', label: '活动进度提醒', description: '活动标记完成或参与者确认完成时提醒你。' },
    ],
  },
];

const NotificationSettings = () => {
  const { data, loading, error, load, isAuthenticated } = usePrivateList(fetchNotificationPreferences, {}, '通知设置加载失败');
  const { busy, run } = usePrivateAction(); const dispatch = useDispatch();
  const [savingKey, setSavingKey] = useState(null);
  const changeSetting = async (key, value) => {
    if (busy || loading) return;
    setSavingKey(key);
    try {
      await run(() => updateNotificationPreferences({ [key]: value }), async () => {
        await load().catch(() => {});
        dispatch(refreshNotificationSummary()).catch(() => {});
        message.success('通知设置已更新');
      }, '通知设置更新失败，请重试');
    } finally { setSavingKey(null); }
  };
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <FeaturePage embedded showDesktopBack={false} className="notificationSettings" title="通知设置" subtitle="选择你想接收的互动与活动提醒，修改后自动保存。">
    {loading && !data ? <div className="notificationSettings-groups">{settingGroups.map(group => <section className="featurePage-panel" key={group.key}><Skeleton active paragraph={{ rows: 4 }} /></section>)}</div>
      : error ? <section className="featurePage-panel"><Alert type="error" showIcon title={error} action={<Button onClick={() => load().catch(() => {})}>重试</Button>} /></section>
      : <div className="notificationSettings-groups">
        {settingGroups.map(group => <section className="featurePage-panel notificationSettings-group" key={group.key} aria-labelledby={`notificationSettings-${group.key}`}>
          <div className="notificationSettings-group-heading"><h2 id={`notificationSettings-${group.key}`}>{group.title}</h2><p>{group.description}</p></div>
          <div className="notificationSettings-options">
            {group.items.map(({ key, label, description }) => {
              const enabled = Boolean(data?.[key]);
              const saving = busy && savingKey === key;
              return <div key={key} className="notificationSettings-option">
                <div className="notificationSettings-copy"><h3>{label}</h3><p id={`notificationSettings-${key}-description`}>{description}</p></div>
                <div className="notificationSettings-control">
                  <span className={`notificationSettings-status${saving ? ' is-saving' : enabled ? ' is-enabled' : ''}`} aria-live="polite">{saving ? '保存中…' : enabled ? '已开启' : '已关闭'}</span>
                  <Switch aria-label={label} aria-describedby={`notificationSettings-${key}-description`} checked={enabled} disabled={busy || loading} loading={saving} onChange={value => changeSetting(key, value)} />
                </div>
              </div>;
            })}
          </div>
        </section>)}
      </div>}
    <section className="featurePage-panel notificationSettings-help" aria-labelledby="notificationSettings-help-heading">
      <div><h2 id="notificationSettings-help-heading">提醒说明</h2><p>关闭提醒后，通知仍保留在互动通知列表中，未读状态保持不变。</p><p>聊天消息的免打扰请在对应会话中单独设置。</p></div>
      <Link className="notificationSettings-notifications-link" to="/message/notifications">查看互动通知</Link>
    </section>
  </FeaturePage>;
};
export default NotificationSettings;
